// src/screens/Auth.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, Lock, ArrowRight,
  ShieldCheck, Zap, Globe, User, Building2,
  ChevronLeft, CheckCircle2, AlertCircle, Link2
} from 'lucide-react';
import Logo from "../img/logo.png";
import './Auth.css';
import { useAuth } from '../contexts/AuthContext';
import API_BASE from '../config/api';

const OAUTH_URLS = {
  google: `${API_BASE}/auth/google/login`,
};

const Auth = ({ onLogin }) => {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [activeMode, setActiveMode] = useState('Login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const [company, setCompany] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || ''
  );
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetSent, setIsResetSent] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [error, setError] = useState('');

  const debug = (...args) => console.log('[Auth Debug]', ...args);

  const clearAuthStorage = () => {
    debug('Clearing auth storage');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    localStorage.removeItem('role');
  };

  const normalizePayload = (raw) => {
    debug('normalizePayload input', raw);

    if (!raw) return null;

    if (typeof raw === 'string') {
      const clean = raw.startsWith('#') || raw.startsWith('?') ? raw.slice(1) : raw;
      const params = new URLSearchParams(clean);

      const payload = {
        access_token: params.get('access_token') || params.get('token') || '',
        refresh_token: params.get('refresh_token') || '',
        user_id: params.get('user_id') || params.get('id') || '',
        role: params.get('role') || '',
        email: params.get('email') || params.get('user_email') || '',
        error: params.get('error') || '',
      };

      debug('normalizePayload parsed string payload', payload);
      return payload;
    }

    if (typeof raw === 'object') {
      const candidate = raw.payload && typeof raw.payload === 'object' ? raw.payload : raw;

      const payload = {
        access_token: candidate.access_token || candidate.token || '',
        refresh_token: candidate.refresh_token || '',
        user_id: candidate.user_id || candidate.id || '',
        role: candidate.role || '',
        email: candidate.email || candidate.user_email || '',
        error: candidate.error || raw.error || '',
        success:
          typeof raw.success === 'boolean'
            ? raw.success
            : !!(candidate.access_token || candidate.token),
        source: raw.source || candidate.source || '',
      };

      debug('normalizePayload parsed object payload', payload);
      return payload;
    }

    return null;
  };

  const saveAuthData = ({ access_token, refresh_token, user_id, role, email }) => {
    debug('Saving auth data', {
      access_token: access_token ? `${access_token.slice(0, 20)}...` : '',
      refresh_token: refresh_token ? `${refresh_token.slice(0, 20)}...` : '',
      user_id,
      role,
      email,
    });

    if (!access_token) {
      debug('saveAuthData skipped: no access_token');
      return false;
    }

    localStorage.setItem('access_token', access_token);
    if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem('user_id', user_id || '');
    localStorage.setItem('role', role || '');
    localStorage.setItem('user_email', email || '');

    return true;
  };

  const completeLogin = ({ access_token, refresh_token, user_id, role, email }) => {
    debug('completeLogin called', {
      access_token: access_token ? `${access_token.slice(0, 20)}...` : '',
      user_id,
      role,
      email,
    });

    if (!access_token) {
      setError('Missing access token.');
      return false;
    }

    saveAuthData({ access_token, refresh_token, user_id, role, email });

    login(
      {
        access_token,
        refresh_token,
        user_id,
        role,
      },
      email || ''
    );

    debug('Navigating to /getstarted');
    navigate('/getstarted', { replace: true });
    return true;
  };

  const readAuthFromStorage = () => {
    const authData = {
      access_token: localStorage.getItem('access_token') || '',
      refresh_token: localStorage.getItem('refresh_token') || '',
      user_id: localStorage.getItem('user_id') || '',
      role: localStorage.getItem('role') || '',
      email: localStorage.getItem('user_email') || '',
    };

    debug('Read auth from storage', {
      access_token: authData.access_token ? `${authData.access_token.slice(0, 20)}...` : '',
      user_id: authData.user_id,
      role: authData.role,
      email: authData.email,
    });

    return authData;
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    const mode = params.get('mode');

    debug('Auth page mounted', {
      path: window.location.pathname,
      search: window.location.search,
      oauthError,
      mode,
    });

    if (mode === 'signup') {
      setActiveMode('SignUp');
    }

    if (oauthError || mode) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (oauthError) {
      debug('OAuth error from query param', oauthError);
      setError(decodeURIComponent(oauthError));
      return;
    }

    const stored = readAuthFromStorage();
    if (stored.access_token) {
      debug('Found existing token in storage, completing login immediately');
      completeLogin(stored);
      return;
    }

    debug('Skipping broken restore endpoints; waiting for popup message or storage fallback');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSocialLogin = (provider) => {
  const url = OAUTH_URLS[provider];
  if (!url) return;

  debug('handleSocialLogin clicked', { provider, url });

  setError('');
  setIsLoading(true);

  const width = 560;
  const height = 720;
  const left = window.screenX + Math.max(0, (window.outerWidth - width) / 2);
  const top = window.screenY + Math.max(0, (window.outerHeight - height) / 2);

  const popup = window.open(
    url,
    'revexia-oauth-popup',
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );

  debug('Popup opened?', !!popup);

  if (!popup) {
    setIsLoading(false);
    setError('Popup blocked. Please allow popups and try again.');
    return;
  }

  let finished = false;
  let storagePollCount = 0;
  let channel = null;

  const cleanup = () => {
    debug('Cleaning up popup listeners');
    window.removeEventListener('message', handleMessage);
    clearTimeout(popupTimeoutTimer);
    clearInterval(storagePollTimer);
    if (channel) channel.close();
    setIsLoading(false);
  };

  const finishWithError = (message) => {
    if (finished) return;
    finished = true;
    debug('finishWithError', message);
    cleanup();
    setError(message || 'Social login failed. Please try again.');
  };

  const finishWithSuccess = (payload) => {
    if (finished) return;
    finished = true;
    debug('finishWithSuccess payload', payload);
    cleanup();

    const accessToken = payload?.access_token || '';
    const refreshToken = payload?.refresh_token || '';
    const userId = payload?.user_id || '';
    const role = payload?.role || '';
    const userEmail = payload?.email || '';

    if (!accessToken) {
      setError('No access token received from social login.');
      return;
    }

    completeLogin({
      access_token: accessToken,
      refresh_token: refreshToken,
      user_id: userId,
      role,
      email: userEmail,
    });

    try {
      popup.close();
    } catch (err) {
      debug('popup.close() failed', err.message);
    }
  };

  const normalizePayload = (raw) => {
    if (!raw) return null;

    if (typeof raw === 'string') {
      const clean = raw.startsWith('#') || raw.startsWith('?') ? raw.slice(1) : raw;
      const params = new URLSearchParams(clean);

      return {
        source: params.get('source') || '',
        access_token: params.get('access_token') || params.get('token') || '',
        refresh_token: params.get('refresh_token') || '',
        user_id: params.get('user_id') || params.get('id') || '',
        role: params.get('role') || '',
        email: params.get('email') || params.get('user_email') || '',
        error: params.get('error') || '',
      };
    }

    if (typeof raw === 'object') {
      const candidate = raw.payload && typeof raw.payload === 'object' ? raw.payload : raw;

      return {
        source: raw.source || candidate.source || '',
        access_token: candidate.access_token || candidate.token || raw.access_token || '',
        refresh_token: candidate.refresh_token || raw.refresh_token || '',
        user_id: candidate.user_id || candidate.id || raw.user_id || '',
        role: candidate.role || raw.role || '',
        email: candidate.email || candidate.user_email || raw.email || '',
        error: candidate.error || raw.error || '',
      };
    }

    return null;
  };

  const looksLikeAuthPayload = (parsed) => {
    if (!parsed) return false;
    return parsed.source === 'oauth-callback' || !!parsed.access_token || !!parsed.error;
  };

  const tryFinishFromStorage = () => {
    const stored = readAuthFromStorage();

    if (!stored.access_token) {
      storagePollCount += 1;
      if (storagePollCount <= 5 || storagePollCount % 10 === 0) {
        debug('Storage fallback check: no token yet', { attempt: storagePollCount });
      }
      return false;
    }

    debug('Recovered auth from storage fallback');
    finished = true;
    cleanup();
    completeLogin(stored);

    try {
      popup.close();
    } catch (err) {
      debug('popup.close() after storage fallback failed', err.message);
    }

    return true;
  };

  const handleParsedPayload = (parsed, sourceLabel) => {
    debug(`Parsed auth payload from ${sourceLabel}`, parsed);

    if (!looksLikeAuthPayload(parsed)) {
      debug(`Ignored ${sourceLabel}: not an auth payload`);
      return;
    }

    if (parsed.error) {
      finishWithError(parsed.error);
      return;
    }

    if (parsed.access_token) {
      finishWithSuccess(parsed);
      return;
    }

    debug(`Ignored ${sourceLabel}: auth-shaped message but no token/error`);
  };

  const handleMessage = (event) => {
    if (event.origin !== window.location.origin) return;

    const parsed = normalizePayload(event.data);

    if (!looksLikeAuthPayload(parsed)) return;

    debug('Received postMessage', {
      origin: event.origin,
      expectedOrigin: window.location.origin,
      data: event.data,
    });

    handleParsedPayload(parsed, 'postMessage');
  };

  window.addEventListener('message', handleMessage);

  if ('BroadcastChannel' in window) {
    try {
      channel = new BroadcastChannel('revexia-oauth');
      channel.onmessage = (event) => {
        const parsed = normalizePayload(event.data);
        if (!looksLikeAuthPayload(parsed)) return;
        debug('Received BroadcastChannel message', event.data);
        handleParsedPayload(parsed, 'BroadcastChannel');
      };
      debug('BroadcastChannel listener attached');
    } catch (err) {
      debug('BroadcastChannel listener failed', err.message);
    }
  }

  const storagePollTimer = setInterval(() => {
    if (finished) return;
    tryFinishFromStorage();
  }, 1000);

  const popupTimeoutTimer = setTimeout(() => {
    if (finished) return;

    debug('Popup auth timeout reached, trying final localStorage fallback');

    if (tryFinishFromStorage()) return;

    finishWithError('Authentication timed out. Please try again.');
  }, 120000);
};

  const callLoginAPI = async () => {
    debug('Calling password login API', { email });

    const params = new URLSearchParams();
    params.append('grant_type', 'password');
    params.append('username', email);
    params.append('password', password);
    params.append('scope', '');
    params.append('client_id', 'string');
    params.append('client_secret', 'string');

    const response = await axios.post(`${API_BASE}/auth/login`, params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    debug('Password login response', response.data);

    const at = response.data.access_token;
    const rt = response.data.refresh_token;

    saveAuthData({
      access_token: at,
      refresh_token: rt,
      user_id: response.data.user_id || '',
      role: response.data.role || '',
      email,
    });

    login(response.data, email);
    return at;
  };

  const handleLogin = async () => {
    await callLoginAPI();
    navigate('/getstarted');
  };

  const handleRegister = async () => {
    if (password !== confirmPassword) throw new Error('Passwords do not match');

    debug('Calling register API', {
      email,
      firstName,
      lastName,
      company,
      mobile,
      timezone,
      siteUrl,
    });

    await axios.post(`${API_BASE}/auth/register`, {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email,
      mobile,
      company,
      timezone,
      site_url: siteUrl,
      password,
    });

    setIsRegistered(true);
  };

  const handleForgotPassword = async () => {
    debug('handleForgotPassword triggered');
    await new Promise((res) => setTimeout(res, 1500));
    setIsResetSent(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (activeMode === 'ForgotPassword') await handleForgotPassword();
      else if (activeMode === 'Login') await handleLogin();
      else await handleRegister();
    } catch (err) {
      debug('handleSubmit error', err);
      const msg =
        err.response?.data?.detail ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        err.message ||
        'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const resetToLogin = () => {
    debug('Resetting auth screen to login');
    setActiveMode('Login');
    setIsResetSent(false);
    setIsRegistered(false);
    setIsLoading(false);
    setError('');
  };

  const GoogleIcon = () => (
    <svg width="20" height="20" viewBox="0 0 48 48" fill="none">
      <path d="M43.611 20.083H42V20H24v8h11.303C33.654 32.657 29.332 36 24 36c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z" fill="#FFC107"/>
      <path d="M6.306 14.691l6.571 4.819C14.655 15.108 19.001 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z" fill="#FF3D00"/>
      <path d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.314 0-9.823-3.415-11.422-8.021l-6.515 5.025C9.505 39.556 16.227 44 24 44z" fill="#4CAF50"/>
      <path d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z" fill="#1976D2"/>
    </svg>
  );

  return (
    <>
      <Helmet>
        <title>
          {activeMode === 'Login'
            ? 'Login'
            : activeMode === 'SignUp'
            ? 'Sign Up'
            : 'Reset Password'} - Revexia AI
        </title>
        <meta name="description" content="Access your revenue intelligence dashboard" />
      </Helmet>

      <div className="auth-page-wrapper">
        <div className="auth-bg-blob auth-bg-blob-1"></div>
        <div className="auth-bg-blob auth-bg-blob-2"></div>

        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-sm-11 col-md-8 col-lg-6 col-xl-5">
              <div className="auth-card">
                <div className="auth-card-top-line"></div>

                {isRegistered ? (
                  <div className="auth-content-animate">
                    <header className="auth-header text-center">
                      <div className="auth-header-icon">
                        <CheckCircle2 size={32} className="text-emerald-400" />
                      </div>
                      <h2 className="auth-title">Verify Your Email</h2>
                      <p className="auth-subtitle">
                        We've sent a verification link to <strong>{email}</strong>.
                        Please check your inbox and verify your account before logging in.
                      </p>
                    </header>
                    <div className="auth-success-message">
                      <p className="auth-success-text">
                        Once verified, return here to log in and start scaling your social commerce ROI.
                      </p>
                    </div>
                    <button onClick={resetToLogin} className="auth-back-btn">
                      <ChevronLeft size={14} className="auth-back-icon" />
                      Return to Login
                    </button>
                  </div>
                ) : activeMode === 'ForgotPassword' ? (
                  <div className="auth-content-animate">
                    <header className="auth-header text-center">
                      <div className="auth-header-icon">
                        {isResetSent
                          ? <CheckCircle2 size={32} className="text-emerald-400" />
                          : <Lock size={32} />}
                      </div>
                      <h2 className="auth-title">
                        {isResetSent ? 'Recovery Sent' : 'Secure Recovery'}
                      </h2>
                      <p className="auth-subtitle">
                        {isResetSent
                          ? `We've dispatched a secure link to ${email || 'your email'}.`
                          : 'Enter your verified business email to receive an AES-256 encrypted reset token.'}
                      </p>
                    </header>

                    {!isResetSent ? (
                      <form onSubmit={handleSubmit} className="auth-form">
                        {error && (
                          <div className="auth-error-banner">
                            <AlertCircle size={15} />
                            <span>{error}</span>
                          </div>
                        )}
                        <div className="auth-input-group">
                          <label className="auth-input-label">Business Email</label>
                          <div className="auth-input-wrapper">
                            <Mail className="auth-input-icon" size={18} />
                            <input
                              type="email"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              placeholder="admin@company.com"
                              className="auth-input-field"
                              required
                            />
                          </div>
                        </div>
                        <button type="submit" disabled={isLoading} className="auth-submit-btn">
                          {isLoading
                            ? <div className="auth-spinner"></div>
                            : <><span>Request Reset Link</span><ArrowRight size={18} className="auth-submit-arrow" /></>}
                        </button>
                      </form>
                    ) : (
                      <div className="auth-success-message">
                        <p className="auth-success-text">
                          Check your inbox and follow the instructions to regain access to your Revexia account.
                        </p>
                      </div>
                    )}

                    <button onClick={resetToLogin} className="auth-back-btn">
                      <ChevronLeft size={14} className="auth-back-icon" />
                      Return to Login
                    </button>
                  </div>
                ) : (
                  <>
                    <header className="auth-header text-center">
                      <img src={Logo} alt="Revexia" className="auth-header-logo" />
                      <h2 className="auth-title">
                        {activeMode === 'Login' ? 'Welcome Back' : 'Join the Network'}
                      </h2>
                      <p className="auth-subtitle">
                        {activeMode === 'Login'
                          ? 'Access your revenue intelligence dashboard'
                          : 'Start scaling your social commerce ROI today'}
                      </p>
                    </header>

                    <div className="auth-tab-group">
                      <button
                        className={`auth-tab ${activeMode === 'Login' ? 'active' : ''}`}
                        onClick={() => { setActiveMode('Login'); setError(''); }}
                      >
                        Login
                      </button>
                      <button
                        className={`auth-tab ${activeMode === 'SignUp' ? 'active' : ''}`}
                        onClick={() => { setActiveMode('SignUp'); setError(''); }}
                      >
                        SignUp
                      </button>
                    </div>

                    <form onSubmit={handleSubmit} className="auth-form">
                      {error && (
                        <div className="auth-error-banner">
                          <AlertCircle size={15} />
                          <span>{error}</span>
                        </div>
                      )}

                      {activeMode === 'SignUp' && (
                        <>
                          <div className="auth-input-group">
                            <label className="auth-input-label">First Name</label>
                            <div className="auth-input-wrapper">
                              <User className="auth-input-icon" size={18} />
                              <input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="Alex"
                                className="auth-input-field"
                                required
                              />
                            </div>
                          </div>

                          <div className="auth-input-group">
                            <label className="auth-input-label">Last Name</label>
                            <div className="auth-input-wrapper">
                              <User className="auth-input-icon" size={18} />
                              <input
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Chen"
                                className="auth-input-field"
                                required
                              />
                            </div>
                          </div>

                          <div className="auth-input-group">
                            <label className="auth-input-label">Company</label>
                            <div className="auth-input-wrapper">
                              <Building2 className="auth-input-icon" size={18} />
                              <input
                                type="text"
                                value={company}
                                onChange={(e) => setCompany(e.target.value)}
                                placeholder="Acme Inc."
                                className="auth-input-field"
                                required
                              />
                            </div>
                          </div>

                          <div className="auth-input-group">
                            <label className="auth-input-label">
                              Store URL <span className="auth-input-hint">(Shopify / WooCommerce)</span>
                            </label>
                            <div className="auth-input-wrapper">
                              <Link2 className="auth-input-icon" size={18} />
                              <input
                                type="url"
                                value={siteUrl}
                                onChange={(e) => setSiteUrl(e.target.value)}
                                placeholder="https://yourstore.myshopify.com"
                                className="auth-input-field"
                                required
                              />
                            </div>
                          </div>

                          {/* <div className="auth-input-group">
                            <label className="auth-input-label">Mobile</label>
                            <div className="auth-input-wrapper">
                              <Phone className="auth-input-icon" size={18} />
                              <input
                                type="tel"
                                value={mobile}
                                onChange={(e) => setMobile(e.target.value)}
                                placeholder="+8801XXXXXXXXX"
                                className="auth-input-field"
                                required
                              />
                            </div>
                          </div>

                          <div className="auth-input-group">
                            <label className="auth-input-label">Timezone</label>
                            <div className="auth-input-wrapper">
                              <Globe className="auth-input-icon" size={18} />
                              <input
                                type="text"
                                value={timezone}
                                onChange={(e) => setTimezone(e.target.value)}
                                placeholder="Asia/Dhaka"
                                className="auth-input-field"
                                required
                              />
                            </div>
                          </div> */}
                        </>
                      )}

                      <div className="auth-input-group">
                        <label className="auth-input-label">Business Email</label>
                        <div className="auth-input-wrapper">
                          <Mail className="auth-input-icon" size={18} />
                          <input
                            type="email"
                            tabIndex={1}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@company.com"
                            className="auth-input-field"
                            required
                          />
                        </div>
                      </div>

                      <div className="auth-input-group">
                        <div className="auth-input-label-row">
                          <label className="auth-input-label">Password</label>
                          {activeMode === 'Login' && (
                            <button
                              type="button"
                              tabIndex={5}
                              onClick={() => setActiveMode('ForgotPassword')}
                              className="auth-forgot-btn"
                            >
                              Forgot?
                            </button>
                          )}
                        </div>
                        <div className="auth-input-wrapper">
                          <Lock className="auth-input-icon" size={18} />
                          <input
                            type="password"
                            tabIndex={2}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="••••••••"
                            className="auth-input-field"
                            required
                          />
                        </div>
                      </div>

                      {activeMode === 'SignUp' && (
                        <div className="auth-input-group">
                          <label className="auth-input-label">Confirm Password</label>
                          <div className="auth-input-wrapper">
                            <Lock className="auth-input-icon" size={18} />
                            <input
                              type="password"
                              tabIndex={3}
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                              placeholder="••••••••"
                              className="auth-input-field"
                              required
                            />
                          </div>
                        </div>
                      )}

                      <button type="submit" tabIndex={4} disabled={isLoading} className="auth-submit-btn">
                        {isLoading
                          ? <div className="auth-spinner"></div>
                          : <>{activeMode === 'Login' ? 'Initialize Session' : 'Create Account'}<ArrowRight size={18} className="auth-submit-arrow" /></>}
                      </button>
                    </form>

                    <div className="auth-divider">
                      <span className="auth-divider-text">Or Continue with</span>
                    </div>

                    <div className="auth-social-buttons">
                      <button
                        type="button"
                        className="auth-social-btn google"
                        onClick={() => handleSocialLogin('google')}
                        disabled={isLoading}
                        title="Continue with Google"
                      >
                        <GoogleIcon />
                        <span className="auth-social-btn-text">Continue with Google</span>
                      </button>
                    </div>
                  </>
                )}

                <footer className="auth-footer">
                  <div className="auth-security-badge">
                    <ShieldCheck size={14} />
                    <span>AES-256 Encrypted Connection</span>
                  </div>
                  <div className="auth-footer-links">
                    <button className="auth-footer-link" type="button">Privacy</button>
                    <span>•</span>
                    <button className="auth-footer-link" type="button">Terms</button>
                    <span>•</span>
                    <button className="auth-footer-link" type="button">Contact</button>
                  </div>
                </footer>
              </div>

              <div className="auth-hints">
                <div className="auth-hint">
                  <Zap size={14} className="auth-hint-icon blue" />
                  <p className="auth-hint-text">98.2% Forecast Accuracy</p>
                </div>
                <div className="auth-hint">
                  <Globe size={14} className="auth-hint-icon purple" />
                  <p className="auth-hint-text">Global Node Status: Optimal</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Auth;